import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useLibraryBrowser } from "./LibraryBrowserContext";

export default function Breadcrumb() {
  const { activePath, navigateTo, sections } = useLibraryBrowser();

  // Resolve path segments into human-readable labels
  const crumbs = resolveCrumbs(activePath, sections);

  return (
    <nav className="flex items-center gap-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-700/30 bg-gray-800/30 min-h-[32px]">
      <button
        onClick={() => navigateTo([])}
        className={
          activePath.length === 0
            ? "text-gray-200 font-medium"
            : "hover:text-gray-300 transition-colors"
        }
      >
        All
      </button>

      {crumbs.map((crumb, i) => (
        <span key={crumb.path.join("/")} className="flex items-center gap-1">
          <ChevronRightIcon className="size-3 text-gray-700" />
          <button
            onClick={() => navigateTo(crumb.path)}
            className={
              i === crumbs.length - 1
                ? "text-gray-200 font-medium"
                : "hover:text-gray-300 transition-colors"
            }
          >
            {crumb.label}
          </button>
        </span>
      ))}
    </nav>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

interface Crumb {
  label: string;
  path: string[];
}

function resolveCrumbs(
  activePath: string[],
  sections: { id: string; label: string; groups: { id: string; label: string; categories?: { id: string; label: string }[] }[] }[]
): Crumb[] {
  const crumbs: Crumb[] = [];
  if (activePath.length === 0) return crumbs;

  // Level 1: section
  const section = sections.find((s) => s.id === activePath[0]);
  if (!section) return crumbs;
  crumbs.push({ label: section.label, path: [section.id] });

  if (activePath.length < 2) return crumbs;

  // Level 2: group
  const group = section.groups.find((g) => g.id === activePath[1]);
  if (!group) return crumbs;
  crumbs.push({ label: group.label, path: [section.id, group.id] });

  if (activePath.length < 3) return crumbs;

  // Level 3: category
  const category = group.categories?.find((c) => c.id === activePath[2]);
  if (!category) return crumbs;
  crumbs.push({ label: category.label, path: [section.id, group.id, category.id] });

  return crumbs;
}
